import {
  Checkbox,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Branch,
  UpdateBranchRequest,
  branchesApi,
} from '@texas/api/endpoints/branchesApi';
import { currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { vatCodesApi } from '@texas/api/endpoints/vatCodesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';

export function BranchModal({
  branch,
  canEdit,
  isOpen,
  onClose,
  onSaved,
}: {
  branch: Branch;
  canEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: currencies, refetch: loadCurrencies } = useApiResource(
    currenciesApi.getForBranch,
  );
  const { data: vatCodes, refetch: loadVatCodes } = useApiResource(
    vatCodesApi.getForBranch,
  );
  const { data: suppliers, refetch: loadSuppliers } = useApiResource(
    branchesApi.getSuppliersForBranch,
  );
  const {
    request: update,
    loading,
    error,
  } = useApiRequest(branchesApi.updateBranch);
  const { register, handleSubmit, reset, control } =
    useForm<UpdateBranchRequest>();

  useEffect(() => {
    if (!isOpen) return;
    loadCurrencies(branch.id);
    loadVatCodes(branch.id);
    loadSuppliers(branch.id);
    reset({
      name: branch.name,
      displayOrder: branch.displayOrder,
      defaultCurrencyId: branch.defaultCurrencyId,
      defaultVatCodeId: branch.defaultVatCodeId,
      enableErpBatching: branch.enableErpBatching ?? false,
      mainInternalSupplierId: branch.mainInternalSupplierId,
      defaultDecimalsInPrice: branch.defaultDecimalsInPrice,
    });
  }, [branch, isOpen, loadCurrencies, loadSuppliers, loadVatCodes, reset]);

  const submit = async (request: UpdateBranchRequest) => {
    const response = await update(branch.id, request);
    if (response.hasError) return;
    toast({ title: t('general.updated'), status: 'success', isClosable: true });
    onSaved();
    onClose();
  };

  const internalSuppliers = (suppliers ?? [])
    .filter((supplier) => supplier.isInternal && !supplier.archived)
    .sort((left, right) => left.name.localeCompare(right.name));

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{branch.name}</ModalHeader>
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <ErrorDetails error={error} />
            <Flex direction="column" gap="4">
              <FormControl isRequired={true}>
                <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                <Input
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('name', { required: true, maxLength: 100 })}
                />
                <HookFormCounter
                  control={control}
                  name="name"
                  maxLength={100}
                />
              </FormControl>
              <FormControl isRequired={true}>
                <TexasFormLabel>
                  {t('configuration.defaultCurrency')}
                </TexasFormLabel>
                <Select
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('defaultCurrencyId', {
                    required: true,
                    valueAsNumber: true,
                  })}
                >
                  {(currencies ?? [])
                    .filter(
                      (currency) =>
                        !currency.archived ||
                        currency.id === branch.defaultCurrencyId,
                    )
                    .map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.name} ({currency.code})
                      </option>
                    ))}
                </Select>
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.defaultVatCode')}
                </TexasFormLabel>
                <Select
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('defaultVatCodeId', {
                    setValueAs: (value) => (value ? Number(value) : 0),
                  })}
                >
                  <option value="">{t('general.none')}</option>
                  {(vatCodes ?? [])
                    .filter(
                      (vatCode) =>
                        !vatCode.archived ||
                        vatCode.id === branch.defaultVatCodeId,
                    )
                    .map((vatCode) => (
                      <option key={vatCode.id} value={vatCode.id}>
                        {vatCode.name} ({vatCode.identifier})
                      </option>
                    ))}
                </Select>
              </FormControl>
              <FormControl>
                <TexasFormLabel>
                  {t('configuration.defaultDecimalsInPrice')}
                </TexasFormLabel>
                <Input
                  type="number"
                  min={0}
                  max={5}
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('defaultDecimalsInPrice', {
                    setValueAs: (value) =>
                      value === '' ? undefined : Number(value),
                  })}
                />
              </FormControl>
              <FormControl>
                <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                <Input
                  type="number"
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('displayOrder', { valueAsNumber: true })}
                />
              </FormControl>
              <Checkbox
                isDisabled={!canEdit}
                {...register('enableErpBatching')}
              >
                {t('configuration.enableGarpBatching')}
              </Checkbox>
              <Text fontSize="sm" color="entityDirectory.mutedText">
                {t('configuration.garpBatchingDescription')}
              </Text>
              <FormControl>
                <TexasFormLabel>
                  {t('branch.defaultInternalSupplier')}
                </TexasFormLabel>
                <Select
                  variant="entityDirectory"
                  isDisabled={!canEdit}
                  {...register('mainInternalSupplierId', {
                    setValueAs: (value) => (value ? Number(value) : 0),
                  })}
                >
                  <option value="">{t('general.none')}</option>
                  {internalSuppliers.map((supplier) => {
                    const number = supplier.branchData.find(
                      (data) => data.branchId === branch.id,
                    )?.supplierNumber;
                    return (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                        {number ? ` (${number})` : ''}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            {canEdit && (
              <SubmitButton loading={loading}>{t('general.save')}</SubmitButton>
            )}
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
